import './EditJob.scss'
import { useState, useMemo, useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { ICreateJobForm, IExitForm, IJobResponse } from 'Models'
import {
  CreateJobStepFourComp,
  CreateJobStepOneComp,
  createJobStepOneSchema,
  CreateJobStepThreeComp,
  createJobStepThreeSchema,
  CreateJobStepTwoComp,
  createJobStepTwoSchema,
} from '../../../../Components'
import employeeService from 'Services/employeeService'
import { handleError, handleSuccess } from 'Helpers'
import { BreadCrumb } from 'Components'
import careerService from 'Services/careerService'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { mapFormToPayload, mapJobToForm } from '../formMapper'
import Swal from 'sweetalert2'
// import logo from '../../../../Assets/Images/logo-dark.jpeg'
// import Swal from 'sweetalert2'

export const EditJob = () => {
  const [countStep, setCountStep] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(false)
  const [data, setData] = useState<any[]>([])

  const navigate = useNavigate()
  const location = useLocation()
  const previousPath = location.state?.from || -1

  const { id } = useParams()
  if (!id) return null

  const defaultValues = {
    department: '',
    jobTitle: '',
    employmentType: '',
    workArrangement: '',
    location: '',
    country: '',
    minimumExperienceYears: 0,
    numberOfOpenings: '',
    hiringManager: '',
    recruiter: '',
    applicationDeadline: '',
    aboutRole: '',
    responsibility: '',
    requirement: '',
    skills: '',
    aboutTradegrid: '',
    customQuestions: [
      {
        title: '',
        type: '',
        required: false,
      },
    ],
  }

  const currentSchema: any = useMemo(() => {
    switch (countStep) {
      case 1:
        return createJobStepOneSchema
      case 2:
        return createJobStepTwoSchema
      case 3:
        return createJobStepThreeSchema
      default:
        return createJobStepOneSchema
    }
  }, [countStep])

  //  This would have been cool but we'd loose each step validation and validate all at the end
  //  So, we'll be using any type on useForm, off course we'd loose type safety but our input validation will still work for each step
  //  const mergedSchema = stepOneSchema.concat(stepTwoSchema).concat(stepThreeSchema);

  const methods = useForm<any>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(currentSchema),
    shouldUnregister: false,
  })

  const { handleSubmit, trigger, reset } = methods

  // const totalSteps = 3
  const usePreview = true
  const totalSteps = usePreview ? 4 : 3

  const steps = usePreview
    ? ['Job Details', 'Job Description', 'Application Form', 'Review & Publish']
    : ['Job Details', 'Job Description', 'Application Form']

  const handlePrevStep = () => setCountStep((prev) => Math.max(prev - 1, 1))

  const handleNextStep = async () => {
    const valid = await trigger()
    if (valid) {
      setCountStep((prev) => Math.min(prev + 1, totalSteps))
    }
  }

  const onSubmit = async (data: ICreateJobForm) => {
    if (countStep !== totalSteps) return
    const payload = mapFormToPayload(data)

    // console.log(payload)
    // console.log(id)
    // return

    setLoading(true)
    // console.log('Form Submitted:', payload)
    // return
    const res = await careerService.updateJob(id, payload).then(
      () => {
        setLoading(false)
        Swal.fire({
          icon: 'success',
          title: 'Done!',
          text: 'Job updated successfully',
          showConfirmButton: false,
          timer: 3000,
        })
        Swal.hideLoading()
        navigate(previousPath)
      },
      (error) => {
        console.log('e', error)
        setLoading(false)
        Swal.hideLoading()
        Swal.fire({
          icon: 'warning',
          title: 'Oops!',
          text: error.response.data.error || 'Something went wrong',
          showConfirmButton: false,
          timer: 3000,
        })
      },
    )
  }

  const fetchData = async () => {
    try {
      const response = await careerService.getSingleJob(id)
      console.log('employee res', response.data.data)
      const dataSet = response.data.data
      reset(mapJobToForm(dataSet))
    } catch (error: any) {
      // console.log('err', error)
      Swal.hideLoading()
      Swal.fire({
        icon: 'warning',
        title: 'Oops!',
        text: error.response.data.error || 'Something went wrong',
        showConfirmButton: false,
        timer: 3000,
      })
    }
  }

  useEffect(() => {
    if (id) {
      fetchData()
    }
  }, [id])

  const fetchUsers = async () => {
    try {
      const response = await employeeService.getAllEmployees()
      // console.log('employee res', response.data.data.dataset)
      const dataSet = response.data.data.dataset
      setData(dataSet)
    } catch (error: any) {
      // console.log('err', error)
      Swal.hideLoading()
      Swal.fire({
        icon: 'warning',
        title: 'Oops!',
        text: error.response.data.error || 'Something went wrong',
        showConfirmButton: false,
        timer: 3000,
      })
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  return (
    <div className='container'>
      <div className='container'>
        <BreadCrumb
          title={'Create Job'}
          parent={'Jobs'}
          parentUrl={'/careers'}
          showTitle={false}
        ></BreadCrumb>
      </div>
      <div className='container text-center form-step-edit'>
        <div className='text-start mt-3'>
          <h5 className='text-capitalize'>Edit Job</h5>
          <p className='set-up-para'>Set up a new opening and configure how candidates apply.</p>

          <div className='steps-progress'>
            {steps.map((step, index) => {
              const stepNumber = index + 1
              const isActive = countStep >= stepNumber

              return (
                <div key={step} className={`step ${isActive ? 'active' : ''}`}>
                  <div className='step-header'>
                    <span className='step-number'>{stepNumber}</span>
                    <span className='step-name'>{step}</span>
                  </div>

                  <div className='step-line' />
                </div>
              )
            })}
          </div>
        </div>
        <div className='card p-3 mb-5 mt-4 border-0'>
          <div className='card-body'>
            <FormProvider {...methods}>
              <form
                // onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                onSubmit={handleSubmit(onSubmit, (err) => {
                  console.log('e', err)
                })}
              >
                {countStep === 1 && <CreateJobStepOneComp data={data} />}
                {countStep === 2 && <CreateJobStepTwoComp />}
                {countStep === 3 && <CreateJobStepThreeComp />}
                {usePreview && countStep === 4 && <CreateJobStepFourComp goToStep={setCountStep} />}

                <div className='mt-3 d-flex justify-content-between gap-5 border-top pt-3'>
                  <div>
                    <button type='submit' className='btn back' disabled={loading}>
                      Save as Draft{' '}
                      {loading && <span className='spinner-border spinner-border-sm'></span>}
                    </button>
                  </div>

                  <div className='d-flex'>
                    {countStep > 1 && (
                      <button
                        type='button'
                        className='btn back'
                        onClick={handlePrevStep}
                        disabled={countStep === 1}
                      >
                        Back
                      </button>
                    )}

                    {countStep === totalSteps && usePreview && (
                      <button type='submit' className='btn btn-default' disabled={loading}>
                        Publish Job{' '}
                        {loading && <span className='spinner-border spinner-border-sm'></span>}
                      </button>
                    )}
                    {countStep < 4 && (
                      <button
                        type='button'
                        className='btn btn-default w-100'
                        onClick={handleNextStep}
                      >
                        Continue to{' '}
                        {countStep == 1
                          ? 'Description'
                          : countStep == 2
                            ? 'Application Form'
                            : 'Review & Publish'}
                      </button>
                    )}
                  </div>
                  {/* {countStep === totalSteps && usePreview ? (
                <button type='submit' className='btn btn-success'>
                  {loading ? 'Submitting...' : 'Submit'}
                </button>
              ) : (
                <button type='button' className='btn btn-primary' onClick={handleNextStep}>
                  Next Step
                </button>
              )} */}
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </div>
    </div>
  )
}
